import { useCallback, useEffect, useRef, useState } from 'react'

import environment from '@/environment'
import { useScript } from '@/hooks/useScript'

import { Spinner } from './ui/spinner'

const GROWIN_SDK_URL = 'https://cdn.meshulam.co.il/sdk/gs.min.js'
const GROWIN_ENVIRONMENT = environment.mode === 'production' ? 'PRODUCTION' : 'DEV'

interface FailureResponse {
  status: number
  message: string
}

declare global {
  interface Window {
    growPayment?: {
      init: (config: {
        environment: 'PRODUCTION' | 'DEV'
        version: number
        events: {
          onSuccess?: (response: unknown) => void
          onFailure?: (response: unknown) => void
          onError?: (response: unknown) => void
          onTimeout?: (response: unknown) => void
          onWalletChange?: (state: 'open' | 'close') => void
          onPaymentStart?: (response: unknown) => void
          onPaymentCancel?: (response: unknown) => void
        }
      }) => Promise<void>
      renderPaymentOptions: (authCode: string) => void
    }
  }
}
interface Props {
  authCode: string
  onSuccess?: () => void
  onError: (error: FailureResponse) => void
  onClose: () => void
}
export const GrowinWalletSDK = ({ authCode, onSuccess, onError, onClose }: Props) => {
  const [sdkError, setSdkError] = useState<string | null>(null)
  const scriptStatus = useScript(GROWIN_SDK_URL)
  const isConfiguredRef = useRef(false)
  const isSucceededRef = useRef(false)
  const configureAndRender = useCallback(async () => {
    if (!authCode || isConfiguredRef.current) {
      return
    }

    // Verify SDK is ready
    if (!window.growPayment) {
      console.warn('Growin Wallet SDK not available')
      setSdkError('Growin Wallet SDK not available')
      onError({ status: 0, message: 'שגיאה בטעינת ארנק התשלום' })
      return
    }

    if (typeof window.growPayment.init !== 'function') {
      console.warn('Growin Wallet SDK init method not available')
      setSdkError('Growin Wallet SDK init method not available')
      onError({ status: 0, message: 'שגיאה בטעינת ארנק התשלום' })
      return
    }

    if (typeof window.growPayment.renderPaymentOptions !== 'function') {
      console.warn('Growin Wallet SDK renderPaymentOptions method not available')
      setSdkError('Growin Wallet SDK renderPaymentOptions method not available')
      onError({ status: 0, message: 'שגיאה בטעינת ארנק התשלום' })
      return
    }

    try {
      // Initialize SDK with configuration
      await window.growPayment.init({
        environment: GROWIN_ENVIRONMENT,
        version: 1,
        events: {
          onSuccess: _response => {
            isConfiguredRef.current = false
            isSucceededRef.current = true
            onSuccess?.()
          },
          onFailure: (response: unknown) => {
            isConfiguredRef.current = false
            const errorResponse = response as FailureResponse
            onError(errorResponse || { status: 0, message: 'שגיאה בתהליך התשלום' })
          },
          onError: (response: unknown) => {
            if (response === 'Wallet not initialized' || response === `SDK was not loaded as needed and therefore can't run`) {
              setTimeout(() => {
                window.growPayment?.renderPaymentOptions(authCode)
              }, 200)
            } else {
              console.error('Growin Wallet payment error:', response)
              isConfiguredRef.current = false
              const errorResponse = response as FailureResponse
              onError(errorResponse || { status: 0, message: 'שגיאה בתהליך התשלום' })
            }
          },
          onTimeout: (response: unknown) => {
            isConfiguredRef.current = false
            const errorResponse = response as FailureResponse
            console.error('Growin Wallet payment timeout:', errorResponse)
            onError(errorResponse || { status: 0, message: 'זמן התשלום פג' })
          },
          onWalletChange: state => {
            console.info('onWalletChange', state)
            if (state === 'close' && !isSucceededRef.current) {
              onClose()
            }
          },
          onPaymentStart: _response => {
            console.info('onPaymentStart', _response)
          },
          onPaymentCancel: _response => {
            console.info('onPaymentCancel', _response)
            // isConfiguredRef.current = false
            // onClose()
          },
        },
      })
      isConfiguredRef.current = true
      window.growPayment?.renderPaymentOptions(authCode)
    } catch (error) {
      console.error('Error initializing Growin Wallet SDK:', error)
      setSdkError('Failed to initialize Growin Wallet SDK')
      onError({ status: 0, message: 'שגיאה באתחול ארנק התשלום' })
    }
  }, [authCode, onSuccess, onError, onClose])

  // Wait for SDK to fully initialize after script is ready
  useEffect(() => {
    if (scriptStatus !== 'ready' || !authCode || isConfiguredRef.current) {
      return
    }

    configureAndRender()
  }, [scriptStatus, authCode, configureAndRender])

  if (sdkError || scriptStatus === 'error') {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <div className='text-center'>
          <p className='text-text-primary mb-2 text-sm font-medium'>שגיאה בטעינת ארנק התשלום</p>
          <p className='text-text-secondary text-xs'>{sdkError || 'שגיאה בטעינת הסקריפט'}</p>
        </div>
      </div>
    )
  }

  if (scriptStatus === 'loading' || scriptStatus === 'idle') {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <div className='text-center'>
          <p className='text-text-primary text-sm'>טוען ארנק תשלום...</p>
        </div>
      </div>
    )
  }

  // SDK will render the payment interface, so we just need a container
  return (
    <div id='growin-wallet-container' className='flex h-full w-full justify-center'>
      <Spinner className='size-12' />
    </div>
  )
}
